/**
 * The reader's language survives a reload (2026-09-28).
 *
 * The switch in the header changed the language in memory only, and `main.tsx` always started in
 * Arabic — so a reload, a pasted link, or coming back from the Ikigai sign-in put an English reader
 * back in Arabic. The choice is kept in `localStorage`: a per-viewer convenience, which is what that
 * store is for. Every read and write is guarded, because a private window or blocked site data makes
 * the accessor throw, and the app must still start — in Arabic, the default (§8).
 *
 * Kept out of `initI18n()` on purpose: about twenty test files call it with no setup, and one reading
 * storage would let an English test leak into the next.
 */
import type { i18n as I18n } from 'i18next';
import { applyDirection, LOCALES, type Locale } from './index.js';

export const LOCALE_STORAGE_KEY = 'tard.locale';

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** The saved language, or Arabic when there is none, it is not one of ours, or storage is unavailable. */
export function restoreLocale(store: Storage | undefined = storage()): Locale {
  try {
    const saved = store?.getItem(LOCALE_STORAGE_KEY);
    return (LOCALES as readonly string[]).includes(saved ?? '') ? (saved as Locale) : 'ar';
  } catch {
    return 'ar';
  }
}

export function saveLocale(locale: Locale, store: Storage | undefined = storage()): void {
  try {
    store?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // A convenience lost, never an error: the reader can choose again.
  }
}

/**
 * Start in the saved language, and keep saving it: direction, `lang` and the page title follow every
 * change. Called once, from `main.tsx`, before the first render.
 *
 * **It awaits the language change, which is what keeps the name of its own test true.** Since slice 8
 * English and French are a fetched chunk rather than a static import, so `changeLanguage` is
 * genuinely asynchronous for them. Left un-awaited, the first render would paint Arabic and flip a
 * tick later — which is the 2026-09-28 defect this function exists to fix, in miniature and harder
 * to see. Arabic is bundled, so an Arabic reader awaits a resolved promise and pays nothing.
 */
export async function startInSavedLocale(
  i18n: I18n,
  store: Storage | undefined = storage(),
): Promise<Locale> {
  const locale = restoreLocale(store);
  const reflect = (current: Locale): void => {
    applyDirection(current);
    document.title = i18n.t('app.title');
  };
  i18n.on('languageChanged', (lng) => {
    const current = (LOCALES as readonly string[]).includes(lng) ? (lng as Locale) : 'ar';
    saveLocale(current, store);
    reflect(current);
  });
  await i18n.changeLanguage(locale);
  reflect(locale);
  return locale;
}
